import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../../base/outline-element/outline-element';
import { ifDefined } from 'lit/directives/if-defined.js';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './sic-nav-link.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import { MenuNavItem } from '../types';
import { getCurrentPath, _dispatchActiveEvent } from '../utility';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../../controllers/screen-size-controller';
/**
 * The SicNavLink component
 * @element sic-nav-link
 */
@customElement('sic-nav-link')
export class SicNavLink extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  screens = new ScreenSizeController(this);

  /**
   * Item data
   */
  @property({ type: Object, attribute: 'item' })
  item: MenuNavItem;

  /**
   * Attribute used to determine if the item is a top level parent item.
   */
  @property({ type: Boolean })
  toplevel: boolean;

  /**
   * ID of parent element.
   */
  @property({ type: String, reflect: false, attribute: 'parent-id' })
  parentID: string;

  /**
   * If screen is below 768px width.
   */
  @property({ type: Boolean })
  isMobile: boolean = window.innerWidth < (screenSizeMap.get('lg') as number);

  firstUpdated() {
    this.checkActive();
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'menu-link': !this.toplevel,
      'menu-link--main': this.toplevel,
      'mobile': this.isMobile,
    };

    return html`
      <span class="${classMap(wrapperClasses)}">
        <a
          aria-label=${ifDefined(this.item.aria)}
          href=${this.item.url}
          @keydown=${this.handleKeyDown}
          @focus=${this.applyActiveAttribute}
          @blur=${this.removeActiveAttribute}
          >${this.item.text}</a
        >
      </span>
    `;
  }

  /**
   * Checks if the item.url matches the window path.
   * If true, sets the active attribute
   * and calls this._dispatchActiveEvent() if the component has a parent element.
   */
  checkActive() {
    if (this.item.url && getCurrentPath(this.item.url)) {
      this.setAttribute('active', 'active');
      if (this.item.parentID) {
        _dispatchActiveEvent(this);
      }
    }
  }

  applyActiveAttribute() {
    this.shadowRoot!.children[0].setAttribute('active', 'active');
  }

  removeActiveAttribute() {
    this.shadowRoot!.children[0].removeAttribute('active');
  }

  /**
   * Prevents scrolling if the space bar is pressed on anchor.
   */
  handleKeyDown(e?: KeyboardEvent) {
    if (e?.type === 'keydown' && e.key === ' ') {
      e.preventDefault();
    }
  }
}
